/**
 * quoteSend.js — Orchestrate the "Send Quote" action.
 *
 * mailto-based MVP: cannot attach files via mailto URLs, so the workflow is
 *   1. Generate the PDF blob
 *   2. Trigger a browser download (file lands in Downloads folder)
 *   3. Open mailto with a body that explicitly tells the client the PDF is
 *      attached (so the user knows to drag it from Downloads into the email)
 *   4. Stamp `quotes.sent_at` and transition `status: draft → sent`
 *   5. Existing quoteSync side-effect updates the linked job to `quoted`
 *
 * Every call site (JobPipeline Step 2, QuoteBrowser detail, QuoteBuilder
 * Save & Send) routes through `prepareQuoteSend` → `SendQuoteModal` →
 * `executeQuoteSend`. The modal owns the user confirmation; this module owns
 * the side effects.
 */

import { generateQuotePdf } from './pdfGenerator';
import { buildEmail } from './emailTemplates';
import { resolveTerms } from './quoteTerms';
import { buildVirtourDisplayLines } from './virtourQuote';
import { fmtC } from './utils';

// Sentinel line dropped into the email body where the deposit payment link goes.
// The real Xero URL is swapped in at send time (executeQuoteSend), so the link
// only exists once the user actually commits to sending.
export const DEPOSIT_LINK_PLACEHOLDER = '[secure payment link — added when you send]';

// ── PDF args reconstruction (matches QuoteBrowser.buildQuotePdfArgs) ──
//
// Sent quotes don't have a builder state object — we synthesise the shape the
// pdfGenerator expects from the saved quote row. Kept in this module so all
// send call sites get identical PDFs.
function buildQuotePdfArgs(quote, settings) {
  const isVirtour = quote.mode === 'virtour';
  const rawLines = (quote.deliverableLines || []).map((dl, i) => ({
    id: i + 1,
    desc: dl.desc || dl.name || '',
    detail: dl.detail || '',
    qty: dl.qty ?? 1,
    rate: dl.rate ?? dl.price ?? dl.amount ?? 0,
    dur: dl.dur || 0,
    source: dl.source || quote.mode,
    ...(isVirtour ? { splitPct: dl.splitPct ?? null } : {}),
    schedule: Array.isArray(dl.schedule) ? dl.schedule : [],
    deliveryDate: dl.deliveryDate || '',
    deliveryTime: dl.deliveryTime || '',
    on: true,
  }));
  // VirTour: printed row amounts are our payout, so they sum to the quote total.
  const lines = isVirtour ? buildVirtourDisplayLines(rawLines) : rawLines;
  const subtotal = lines.reduce((s, l) => s + (l.qty || 1) * (l.rate || 0), 0);
  const discAmt = parseFloat(quote.discountAmt) || 0;
  const discPct = parseFloat(quote.discountPct) || 0;
  const discountAmount = discAmt || (discPct ? (subtotal * discPct) / 100 : 0);
  const total = subtotal - discountAmount;

  const state = {
    quoteNumber: quote.quoteNumber || '',
    quoteDate: quote.createdAt ? quote.createdAt.split('T')[0] : '',
    validUntil: quote.expiresAt ? quote.expiresAt.split('T')[0] : '',
    client: quote.clientName || '',
    email: quote.clientEmail || '',
    phone: '',
    address: quote.calcSnapshot?.address || quote.calcSnapshot?.location || '',
    originator: quote.calcSnapshot?.originator || '',
    preparedBy: quote.calcSnapshot?.preparedBy || '',
    notes: quote.notes || '',
    mode: quote.mode || 'custom',
    lines,
    discountType: discPct > 0 ? 'pct' : 'flat',
    discountValue: discPct > 0 ? discPct : discAmt || 0,
    discountReason: quote.discountReason || '',
  };
  const computed = {
    subtotal,
    discountAmount,
    total,
    lineCount: lines.length,
    estimatedDuration: 0,
  };
  const activeTerms = resolveTerms(quote, settings);
  return { state, computed, activeTerms, settings };
}

// ── Build a job-shape object the email template engine can consume ──
//
// The `quote_sent` template reads from `job.calcSnapshot.*` (via
// buildContext + buildServiceSchedule) — when sending directly from a quote
// without a job we synthesise the minimal shape the template needs.
function buildJobShape(quote, job) {
  if (job) return job;
  return {
    title: quote.clientName || '',
    clientName: quote.clientName || '',
    contactEmail: quote.clientEmail || '',
    contactName: quote.calcSnapshot?.contact || quote.clientName || '',
    location: quote.calcSnapshot?.address || quote.calcSnapshot?.location || '',
    calcSnapshot: quote.calcSnapshot || {},
    revenue: quote.total || 0,
    jobType: quote.mode === 're' ? 'Real Estate' : (quote.mode === 'virtour' ? 'VirTour' : ''),
    discountAmt: quote.discountAmt,
    discountPct: quote.discountPct,
  };
}

// ── Build a JOB payload from a quote (ensure-a-job-on-send) ──
//
// When a quote is sent without a linked job, we mint one so the opportunity
// lands on the job board and a deposit invoice has something to link to. Mirrors
// the essentials of useQuoteBuilder.buildJobData — crucially it carries
// `calcSnapshot` / `revenue` / `quoteTerms` so payout-ledger seeding and later
// invoices work (not a thin stub). Status is 'quoted' since we're sending now.
export function buildJobFromQuote(quote) {
  const snap = quote.calcSnapshot || {};
  return {
    title: snap.address || snap.location || quote.clientName || 'Untitled',
    client: quote.clientName || '',
    contact: snap.contact || quote.clientName || '',
    email: quote.clientEmail || '',
    phone: snap.phone || '',
    type: quote.mode === 're' ? 'Real Estate' : (quote.mode === 'virtour' ? 'VirTour' : (snap.jobType || '')),
    location: snap.address || snap.location || '',
    revenue: quote.total || 0,
    originator: snap.originator || '',
    contactId: quote.contactId || null,
    notes: quote.notes || '',
    propertyType: snap.propertyType || '',
    bedrooms: snap.bedrooms || null,
    bathrooms: snap.bathrooms || null,
    sqft: snap.sqft || null,
    deliverables: quote.deliverableLines || [],
    discountPct: quote.discountPct || 0,
    discountAmt: quote.discountAmt || 0,
    discountReason: quote.discountReason || '',
    quoteTerms: quote.terms || [],
    calcSnapshot: quote.calcSnapshot || {},
    quoteId: quote.id,
    status: 'quoted',
    source: 'quote',
  };
}

/**
 * Build everything needed for the SendQuoteModal — does NOT trigger any side
 * effects. Pure assembly so the modal can show a preview, let the user edit
 * subject/CC, etc., before executeQuoteSend fires.
 *
 * @returns {{ defaultToEmail, defaultSubject, defaultBody, pdfFilename, isResend }}
 */
export function prepareQuoteSend({ quote, job = null, settings, senderName, withDeposit = false, depositAmount = 0, depositPct = null }) {
  const jobShape = buildJobShape(quote, job);
  // Resend awareness is informational only (the modal shows an "already sent"
  // note). We intentionally do NOT prefill any "(REMINDER)" framing — every
  // send composes the same clean email.
  const isResend = !!quote.sentAt;

  // When the deposit option is on, the closing CTA becomes a direct
  // "here's your deposit invoice: <placeholder>" line (link swapped at send).
  const depositRequest = withDeposit
    ? { amount: fmtC(depositAmount), placeholder: DEPOSIT_LINK_PLACEHOLDER }
    : null;

  let { subject, body } = buildEmail('quote_sent', { // eslint-disable-line prefer-const
    quote: { number: quote.quoteNumber, total: quote.total },
    job: jobShape,
    settings,
    senderName,
    depositRequest,
  });

  // When a deposit invoice rides along, flag it in the subject so the client
  // sees it's a quote *and* a deposit to pay. Robust to custom subject
  // templates: tag the word "Quote" if present, else prepend.
  if (withDeposit) {
    subject = /\bQuote\b/i.test(subject)
      ? subject.replace(/\bQuote\b/i, 'Quote & Deposit')
      : `Quote & Deposit · ${subject}`;
  }

  const finalSubject = subject;

  // Filename matches what pdfGenerator emits so the user can find it.
  const clientName = (quote.clientName || 'Client').replace(/[^a-zA-Z0-9]/g, '_');
  const pdfFilename = `Quote_${quote.quoteNumber || 'Draft'}_${clientName}.pdf`;

  // Prefix the body with an explicit attachment instruction so the recipient
  // (and the sender) know the PDF needs to be drag-and-dropped in.
  const attachmentBlurb = `📎 Your quote PDF (${pdfFilename}) just downloaded to your computer — please attach it to this email before sending.`;
  const bodyWithBlurb = `${attachmentBlurb}\n\n${body}`;

  return {
    defaultToEmail: quote.clientEmail || jobShape.contactEmail || '',
    defaultSubject: finalSubject,
    defaultBody: bodyWithBlurb,
    pdfFilename,
    isResend,
    // Helpers exposed so the modal doesn't need its own imports. The deposit
    // (when toggled) is reflected in the PDF's totals as a due-now/balance split.
    _buildPdf: () => generateQuotePdf({
      ...buildQuotePdfArgs(quote, settings),
      deposit: withDeposit ? { amount: depositAmount, pct: depositPct } : null,
      returnBlob: true,
    }),
  };
}

// ── Browser-safe blob download (mailto can't attach, so user attaches manually) ──
async function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  // Give the browser a tick to start the download before revoking the URL.
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Build a mailto: URL that includes an optional CC. Mirrors
 * emailTemplates.buildMailtoUrl but with cc + bcc support.
 */
function buildMailtoWithCc(toEmail, { cc, subject, body }) {
  const parts = [];
  if (cc) parts.push(`cc=${encodeURIComponent(cc)}`);
  if (subject) parts.push(`subject=${encodeURIComponent(subject)}`);
  if (body) parts.push(`body=${encodeURIComponent(body)}`);
  return `mailto:${toEmail || ''}${parts.length ? '?' + parts.join('&') : ''}`;
}

/**
 * Execute the user's confirmed send. Steps are independently togglable so the
 * modal's checkboxes can skip parts (e.g. "Just open the email", no download).
 *
 * @returns Promise<{ pdfDownloaded, mailtoOpened, statusUpdated }>
 */
export async function executeQuoteSend({
  quote,
  prep,                       // result of prepareQuoteSend()
  toEmail,                    // user-edited
  cc = '',                    // optional, comma-separated
  subject,                    // user-edited
  body,                       // user-edited
  doDownload = true,
  doMailto = true,
  doStampSent = true,
  updateQuoteAsync,           // (vars) => Promise — typically updateQuote.mutateAsync
  ensureJobAsync,             // optional async () => jobId — creates+links a job if none
  deposit = null,             // optional { amount, placeholder } when a deposit is requested
  createDepositAsync,         // optional async (jobId) => { link } — create invoice + Xero sync
  settings,                   // for the payment-link fallback
  onWarn,                     // optional (msg) => void — non-fatal warnings
}) {
  const result = { pdfDownloaded: false, mailtoOpened: false, statusUpdated: false, jobId: quote.jobId || null, depositIncluded: false };

  // 0. Ensure a job exists so the opportunity reaches the board and a deposit
  //    invoice has something to link to. No-op when one is already linked
  //    (Job Pipeline sends, resends). Tied to an actual send, not a bare download.
  if (ensureJobAsync && (doMailto || doStampSent || deposit)) {
    try { result.jobId = await ensureJobAsync(); }
    catch (err) { onWarn?.(`Could not create the job: ${err.message || 'unknown error'}`); }
  }

  // 0b. Deposit is ATOMIC: resolve a real invoice + link, or abort the whole
  //     send. We never ship an email/PDF advertising a deposit that doesn't
  //     exist. (The modal already gates withDeposit and blocks "use existing"
  //     with no invoice, so a deposit reaching here is create-mode or
  //     existing-with-invoice; these throws are the safety net for a mid-send
  //     failure such as job creation falling over.)
  let finalBody = body;
  if (deposit) {
    const fallback = settings?.data?.paymentLink || settings?.paymentLink || 'a secure payment link will follow by email';
    if (!result.jobId) {
      throw new Error('Could not create or find a job for the deposit invoice — quote not sent. Retry, or uncheck the deposit option to send without it.');
    }
    const r = await createDepositAsync(result.jobId); // create/sync; may throw → surfaced by the caller
    if (r?.missing) {
      throw new Error('No deposit invoice could be resolved — quote not sent. Choose "Create", or make one in the Job Pipeline first.');
    }
    const link = r?.link || '';
    if (link) result.depositIncluded = true;
    else onWarn?.('Deposit invoice ready, but the Xero payment link could not be generated — a placeholder note was used.');
    const payLink = link || fallback;
    if (body.includes(deposit.placeholder)) {
      finalBody = body.split(deposit.placeholder).join(payLink);
    } else {
      // Placeholder edited out, or a custom template lacks {{closingCta}} —
      // append the pay line so a deposit email never ships without a way to pay.
      finalBody = `${body}\n\nPay online (card): ${payLink}`;
      onWarn?.("The payment-link placeholder wasn't in the email body — appended the pay link so the client can still pay.");
    }
  }

  // 1. Download PDF (do this first so the file is in Downloads by the time
  //    the mail app opens — otherwise the user may try to attach before the
  //    file finishes writing).
  if (doDownload) {
    const { blob, filename } = await prep._buildPdf();
    await triggerDownload(blob, filename || prep.pdfFilename);
    result.pdfDownloaded = true;
  }

  // 2. Open mailto. Browser may block if not triggered by user gesture, but
  //    the modal's confirm-click *is* a user gesture so we're fine.
  if (doMailto) {
    const url = buildMailtoWithCc(toEmail, { cc, subject, body: finalBody });
    window.location.href = url;
    result.mailtoOpened = true;
  }

  // 3. Stamp sent_at + transition status. Resends don't touch sent_at to
  //    preserve the original timestamp (mirrors invoice resend pattern).
  if (doStampSent && updateQuoteAsync) {
    const isFirstSend = !quote.sentAt;
    const updates = { id: quote.id };
    if (isFirstSend) {
      updates.sentAt = new Date().toISOString();
    }
    // Promote from draft → sent on first send OR resend (defensive — quoteSync
    // should catch this elsewhere too but better to be explicit).
    if (quote.status === 'draft') updates.status = 'sent';
    if (Object.keys(updates).length > 1) {
      await updateQuoteAsync(updates);
      result.statusUpdated = true;
    }
  }

  return result;
}
